import { RouteProp } from '@react-navigation/native';
import React, { useMemo } from 'react';
import { ProfilesTabParamList, useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import useNotes from '../../../../../hooks/api/useNotes';
import useMedicalProfiles from '../../../../../hooks/api/useMedicalProfiles';
import { getIntl } from '../../../../../utils/intl';
import getName from '../../../../../utils/getName';
import { usePromptButton } from '../../../../shared/PromptButton';
import Select from '../../../../v2BaseComponents/Inputs/Select';
import { SelectOption } from '../../../../v2BaseComponents/Inputs/Select/types';
import { ContentStack } from '../../../../v2BaseComponents/Stack';
import Text from '../../../../v2BaseComponents/Text';
import { PromptActions } from '../../../../v2BaseComponents/Prompt';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';
import MainContentContainer from '../../../../v2BaseComponents/MainContentContainer';
import ProfileHeaderContainer from '../../../../v2BaseComponents/ProfileHeaderContainer';
import { CenteredFormRow } from '../../../../v2BaseComponents/Form';
import { useProfilesWithFileLimits } from '../../../../../hooks/api/useMedicalProfileUsageMetric';

const intl = getIntl('notes-details', {
    headerText:
        'Copy this note to another Medical Profile.  Note that this will create a new note and will only copy the title, content, attachments, and tags.  Comment history will not be copied.',
    confirmationTitle: 'Are you sure?',
    confirmationText:
        "Notes can contain sensitive information.  This information will be viewable by collaborators on the {name}'s profile.",
    cancel: 'Cancel',
    confirm: 'Copy',
    copyToProfile: 'Copy to Profile',
});

export type CopyNoteOwnProps = {};

export type CopyNoteProps = CopyNoteOwnProps & {
    route: RouteProp<ProfilesTabParamList, 'copy-note'>;
};

const CopyNote: React.FC<CopyNoteProps> = (props) => {
    const { noteId, profileId } = props.route.params;
    const { isLoadingProfiles, medicalProfiles } = useMedicalProfiles({});
    const { navigate } = useProfilesTabNavigation();
    const { isLoadingNote, note, copyNote, isCopyingNote } = useNotes({
        noteId: noteId,
        onNoteCopySuccess: (note) => {
            navigate('note', {
                noteId: note.noteId,
                noteEntityType: note.entityType,
                profileId: props.route.params.profileId,
            });
        },
    });
    const [selectedProfileId, setSelectedProfileId] = React.useState<string | null>(null);
    const medicalProfileIds = medicalProfiles?.map((_) => _.medicalProfileId) || [];
    const profilesWithFileLimitsRes = useProfilesWithFileLimits({ medicalProfileIds });
    const options = useMemo<SelectOption[]>(
        () =>
            (medicalProfiles || [])
                .filter((_) => _.medicalProfileId !== profileId)
                .filter((_) => {
                    if (!note?.files.length && !note?.images.length) return true;

                    const targetLimit = profilesWithFileLimitsRes.profilesWithFileLimits?.find(
                        (p) => p.medicalProfileId === _.medicalProfileId,
                    );
                    if (!targetLimit) return false;

                    return !targetLimit.hasReachedFileUploadLimit;
                })
                .map<SelectOption>((_) => ({
                    label: getName(_),
                    value: _.medicalProfileId,
                    testID: getName(_),
                }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [
            medicalProfiles,
            note?.files.length,
            note?.images.length,
            profileId,
            profilesWithFileLimitsRes.profilesWithFileLimits,
        ],
    );

    const selectedProfile = medicalProfiles?.find((_) => _.medicalProfileId === selectedProfileId);
    const { Prompt, onPress } = usePromptButton({
        renderPromptContents: (onClose) => (
            <ContentStack>
                <Text variant="body-1">
                    {intl('confirmationText', { name: selectedProfile ? getName(selectedProfile) : '' })}
                </Text>
                <PromptActions
                    actions={[
                        {
                            title: intl('confirm'),
                            onPress: () => {
                                onClose();
                                copyNote({
                                    sourceProfileId: profileId,
                                    destinationProfileId: selectedProfileId!,
                                    noteId: noteId,
                                });
                            },
                            icon: 'Copy',
                            testID: 'copy-note-confirm-button',
                        },
                        {
                            title: intl('cancel'),
                            onPress: onClose,
                            variant: 'secondary',
                            icon: 'XCircle',
                            testID: 'copy-note-cancel-button',
                        },
                    ]}
                />
            </ContentStack>
        ),
        promptTitle: intl('confirmationTitle'),
    });

    return (
        <ProfileHeaderContainer profileId={profileId} bottomBorder omitChatButton>
            <AnimateLoadedContent
                isLoading={
                    isLoadingNote ||
                    isLoadingProfiles ||
                    !note ||
                    !medicalProfiles ||
                    isCopyingNote ||
                    profilesWithFileLimitsRes.isLoading
                }
            >
                {() => (
                    <>
                        <ActionButtonLayout
                            whiteBackground
                            scrollable
                            actions={[
                                {
                                    title: intl('copyToProfile'),
                                    icon: 'Copy',
                                    onPress: onPress,
                                    testID: 'copy-note-submit-button',
                                    isDisabled: !selectedProfileId,
                                },
                            ]}
                        >
                            <MainContentContainer>
                                <ContentStack>
                                    <CenteredFormRow>
                                        <Text variant="body-1">{intl('headerText')}</Text>
                                    </CenteredFormRow>
                                    <CenteredFormRow>
                                        <Select
                                            title={intl('copyToProfile')}
                                            selectOptions={options}
                                            value={selectedProfileId}
                                            onChange={setSelectedProfileId}
                                            testID="copy-note-profile-select"
                                        />
                                    </CenteredFormRow>
                                </ContentStack>
                            </MainContentContainer>
                        </ActionButtonLayout>
                        {Prompt}
                    </>
                )}
            </AnimateLoadedContent>
        </ProfileHeaderContainer>
    );
};

export default React.memo(CopyNote);
